import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { SearchConditionCard } from "@/components/SearchConditionCard";
import { format, isValid, parseISO } from "date-fns";
import { DateRangePicker } from "@/components/DatePicker";
import { usePrefectures } from "@/hooks/usePrefectures";
import { type AcquiredPropertyFilters } from "../types";
import { EMPTY_FILTERS, hasSearchFilters } from "../utils/searchState";

interface Props {
  applied: AcquiredPropertyFilters;
  onSearch: (filters: AcquiredPropertyFilters) => void;
  onClear: () => void;
  isLoading?: boolean;
}

type DateKey = "acquiredDateFrom" | "acquiredDateTo";
type DraftFilters = Omit<AcquiredPropertyFilters, DateKey> & {
  acquiredDateRange: [Date | null, Date | null];
};

const toDraft = ({
  acquiredDateFrom,
  acquiredDateTo,
  ...filters
}: AcquiredPropertyFilters): DraftFilters => ({
  ...filters,
  acquiredDateRange: [
    acquiredDateFrom ? parseISO(acquiredDateFrom) : null,
    acquiredDateTo ? parseISO(acquiredDateTo) : null,
  ],
});

const validateDateRange = ([start, end]: DraftFilters["acquiredDateRange"]):
  | true
  | string => {
  if ([start, end].some((date) => date !== null && !isValid(date))) {
    return "取得日を正しい日付で入力してください。";
  }
  return (
    !start ||
    !end ||
    start <= end ||
    "取得日の終了日は開始日以降を指定してください。"
  );
};

export const SearchForm: React.FC<Props> = ({
  applied,
  onSearch,
  onClear,
  isLoading = false,
}) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<DraftFilters>({
    defaultValues: toDraft(applied),
    reValidateMode: "onSubmit",
  });
  const { prefectures, isError: prefecturesError } = usePrefectures();
  useEffect(() => {
    reset(toDraft(applied));
  }, [applied, reset]);

  const submit = ({
    acquiredDateRange: [start, end],
    ...filters
  }: DraftFilters): void => {
    if (isLoading) return;
    const next = {
      ...filters,
      acquiredDateFrom: start ? format(start, "yyyy-MM-dd") : "",
      acquiredDateTo: end ? format(end, "yyyy-MM-dd") : "",
    };
    if (!hasSearchFilters(next)) {
      setError("root", {
        message: "検索条件を1つ以上指定してください（複数条件はAND検索）",
      });
      return;
    }
    onSearch(next);
  };
  const error = errors.acquiredDateRange?.message;

  return (
    <SearchConditionCard>
      <Box
        component="form"
        aria-label="検索条件"
        onSubmit={(event) => {
          void handleSubmit(submit)(event);
        }}
        sx={{ px: { xs: 2, sm: 3, md: 4 }, mt: 3, mb: 2.5 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="識別名"
              placeholder="識別名を入力"
              InputLabelProps={{ shrink: true }}
              {...register("identificationName", {
                onChange: () => {
                  clearErrors();
                },
              })}
              inputProps={{ "aria-label": "識別名", maxLength: 256 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink id="acquired-prefecture-label">
                都道府県
              </InputLabel>
              <Controller
                name="prefectureCode"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="acquired-prefecture-label"
                    onChange={(event) => {
                      field.onChange(event);
                      clearErrors();
                    }}
                    input={<OutlinedInput label="都道府県" notched />}
                    inputProps={{ "aria-label": "都道府県" }}
                    displayEmpty
                  >
                    <MenuItem value="">全て</MenuItem>
                    {prefectures.map((prefecture) => (
                      <MenuItem key={prefecture.id} value={prefecture.prefCode}>
                        {prefecture.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="所在"
              placeholder="所在地を入力"
              InputLabelProps={{ shrink: true }}
              {...register("location", {
                onChange: () => {
                  clearErrors();
                },
              })}
              inputProps={{ "aria-label": "所在", maxLength: 512 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="地番または家屋番号"
              placeholder="地番や家屋番号で検索"
              InputLabelProps={{ shrink: true }}
              {...register("chibanKaokuNumber", {
                onChange: () => {
                  clearErrors();
                },
              })}
              inputProps={{
                "aria-label": "地番または家屋番号",
                maxLength: 256,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Controller
                name="acquiredDateRange"
                control={control}
                rules={{ validate: validateDateRange }}
                render={({ field: { value, onChange } }) => (
                  <DateRangePicker
                    muiSize="small"
                    startDateLabel="取得日 開始"
                    endDateLabel="取得日 終了"
                    startDate={value[0]}
                    endDate={value[1]}
                    onChangeStartDate={(date) => {
                      onChange([date, value[1]]);
                      clearErrors();
                    }}
                    onChangeEndDate={(date) => {
                      onChange([value[0], date]);
                      clearErrors();
                    }}
                  />
                )}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              取得日は元のPDFを入手した日です。リユースした日とは異なります。
            </Typography>
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {prefecturesError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            都道府県を読み込めませんでした。画面を再読み込みしてください。
          </Alert>
        )}
        {errors.root?.message && (
          <FormHelperText error sx={{ mt: 1 }}>
            {errors.root.message}
          </FormHelperText>
        )}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          <Button
            type="button"
            disabled={isLoading}
            size="small"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => {
              reset(toDraft(EMPTY_FILTERS));
              onClear();
            }}
          >
            クリア
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            size="small"
            variant="contained"
            startIcon={<SearchIcon />}
          >
            {isLoading ? "検索中…" : "この条件で検索"}
          </Button>
        </Stack>
      </Box>
    </SearchConditionCard>
  );
};
